/**
 * Lightweight sound effects system using Web Audio API.
 * All sounds are generated programmatically — no external audio files needed.
 * Designed for kid-friendly game feedback (ages 3-10).
 */

export type SoundType =
  | 'correct'
  | 'incorrect'
  | 'flip'
  | 'match'
  | 'complete'
  | 'tap'
  | 'levelup'
  | 'streak'
  | 'coin'
  | 'star';

const STORAGE_KEY = 'homeschool-sound';

let audioCtx: AudioContext | null = null;
let soundEnabled: boolean | null = null;

function getAudioContext(): AudioContext | null {
  if (!audioCtx) {
    try {
      audioCtx = new AudioContext();
    } catch {
      return null;
    }
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
}

function readEnabled(): boolean {
  if (soundEnabled !== null) return soundEnabled;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    soundEnabled = stored === null ? true : stored === 'true';
  } catch {
    soundEnabled = true;
  }
  return soundEnabled;
}

export function isSoundEnabled(): boolean {
  return readEnabled();
}

export function setSoundEnabled(enabled: boolean): void {
  soundEnabled = enabled;
  try {
    localStorage.setItem(STORAGE_KEY, String(enabled));
  } catch {
    // localStorage unavailable
  }
}

// ─── Tone helpers ───

function tone(
  ctx: AudioContext,
  frequency: number,
  startTime: number,
  duration: number,
  volume: number = 0.15,
  type: OscillatorType = 'sine',
): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(frequency, startTime);
  gain.gain.setValueAtTime(volume, startTime);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(startTime);
  osc.stop(startTime + duration);
}

function noise(
  ctx: AudioContext,
  startTime: number,
  duration: number,
  volume: number = 0.04,
): void {
  const bufferSize = ctx.sampleRate * duration;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * 0.5;
  }
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(volume, startTime);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
  // Low-pass filter for a soft whoosh
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(2000, startTime);
  filter.frequency.exponentialRampToValueAtTime(500, startTime + duration);
  src.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  src.start(startTime);
  src.stop(startTime + duration);
}

// ─── Sound definitions ───

const SOUNDS: Record<SoundType, (ctx: AudioContext) => void> = {
  correct(ctx) {
    // Bright rising chime — two quick ascending tones
    const t = ctx.currentTime;
    tone(ctx, 523, t, 0.12, 0.15, 'sine');        // C5
    tone(ctx, 784, t + 0.08, 0.18, 0.15, 'sine');  // G5
  },

  incorrect(ctx) {
    // Soft low buzz — gentle, not scary
    const t = ctx.currentTime;
    tone(ctx, 200, t, 0.25, 0.08, 'sine');
    tone(ctx, 180, t + 0.05, 0.2, 0.06, 'sine');
  },

  flip(ctx) {
    // Quick whoosh/click
    const t = ctx.currentTime;
    noise(ctx, t, 0.08, 0.06);
    tone(ctx, 1200, t, 0.05, 0.06, 'sine');
  },

  match(ctx) {
    // Happy ding
    const t = ctx.currentTime;
    tone(ctx, 880, t, 0.2, 0.15, 'sine');       // A5
    tone(ctx, 1108, t + 0.05, 0.25, 0.1, 'sine'); // C#6 — major third
  },

  complete(ctx) {
    // Celebration fanfare — ascending arpeggio
    const t = ctx.currentTime;
    tone(ctx, 523, t, 0.15, 0.12, 'sine');           // C5
    tone(ctx, 659, t + 0.1, 0.15, 0.12, 'sine');     // E5
    tone(ctx, 784, t + 0.2, 0.15, 0.12, 'sine');     // G5
    tone(ctx, 1047, t + 0.3, 0.3, 0.15, 'sine');     // C6
    tone(ctx, 1047, t + 0.3, 0.3, 0.08, 'triangle'); // shimmer
  },

  tap(ctx) {
    // Subtle click
    const t = ctx.currentTime;
    tone(ctx, 800, t, 0.04, 0.06, 'sine');
  },

  levelup(ctx) {
    // Triumphant ascending chord
    const t = ctx.currentTime;
    tone(ctx, 392, t, 0.2, 0.1, 'sine');             // G4
    tone(ctx, 494, t + 0.1, 0.2, 0.1, 'sine');       // B4
    tone(ctx, 587, t + 0.2, 0.2, 0.1, 'sine');       // D5
    tone(ctx, 784, t + 0.3, 0.35, 0.14, 'sine');     // G5
    tone(ctx, 784, t + 0.3, 0.35, 0.07, 'triangle'); // shimmer
  },

  streak(ctx) {
    // Quick ascending beeps
    const t = ctx.currentTime;
    tone(ctx, 600, t, 0.08, 0.1, 'square');
    tone(ctx, 800, t + 0.08, 0.08, 0.1, 'square');
    tone(ctx, 1000, t + 0.16, 0.08, 0.1, 'square');
    tone(ctx, 1200, t + 0.24, 0.12, 0.1, 'square');
  },

  coin(ctx) {
    // Mario-like coin — two quick tones
    const t = ctx.currentTime;
    tone(ctx, 988, t, 0.06, 0.15, 'square');       // B5
    tone(ctx, 1319, t + 0.06, 0.3, 0.12, 'square'); // E6
  },

  star(ctx) {
    // Sparkle/twinkle
    const t = ctx.currentTime;
    tone(ctx, 1397, t, 0.1, 0.08, 'sine');         // F6
    tone(ctx, 1760, t + 0.06, 0.08, 0.06, 'sine'); // A6
    tone(ctx, 2093, t + 0.12, 0.15, 0.1, 'sine');  // C7
    tone(ctx, 1760, t + 0.2, 0.12, 0.05, 'sine');  // A6 echo
  },
};

// ─── Public API ───

export function playSound(type: SoundType): void {
  if (!readEnabled()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    SOUNDS[type](ctx);
  } catch {
    // Graceful fail — sound is non-critical
  }
}

// Pitch maps to vertical pad position so audio reinforces memory:
// 0 (top) → C5, 1 (right) → G4, 2 (left) → E4, 3 (bottom) → C4
const PATTERN_PAD_FREQUENCIES = [523, 392, 330, 262];

export function playPatternTone(padIndex: number): void {
  if (!readEnabled()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    const freq = PATTERN_PAD_FREQUENCIES[padIndex] ?? 440;
    tone(ctx, freq, ctx.currentTime, 0.3, 0.18, 'sine');
  } catch {
    // Graceful fail
  }
}
